import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GitBranch, ChevronDown, ChevronUp, Copy, Check } from "lucide-react";

export type WorkflowStep = {
  method: string;
  path: string;
  summary: string;
  color: string;
};

export type Workflow = {
  name: string;
  description: string;
  steps: WorkflowStep[];
  prompt: string;
  pattern: string;
};

const METHOD_COLORS: Record<string, string> = {
  GET:    "bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300",
  POST:   "bg-green-100 text-green-700 dark:bg-green-950/60 dark:text-green-300",
  PUT:    "bg-orange-100 text-orange-700 dark:bg-orange-950/60 dark:text-orange-300",
  PATCH:  "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300",
  DELETE: "bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300",
};

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };
  return (
    <button
      type="button"
      onClick={copy}
      title="Copy prompt"
      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium bg-muted/60 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
    >
      <AnimatePresence mode="wait" initial={false}>
        {copied ? (
          <motion.span key="check" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex items-center gap-1">
            <Check className="w-3 h-3 text-emerald-500" /> Copied
          </motion.span>
        ) : (
          <motion.span key="copy" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex items-center gap-1">
            <Copy className="w-3 h-3" /> Copy prompt
          </motion.span>
        )}
      </AnimatePresence>
    </button>
  );
}

function WorkflowCard({ workflow, index }: { workflow: Workflow; index: number }) {
  const [open, setOpen] = useState(index === 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08, duration: 0.4 }}
      className="glass-strong rounded-xl border border-border/60 overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex gap-0.5 shrink-0">
            {workflow.steps.slice(0, 4).map((s, i) => (
              <span
                key={i}
                className={`text-[8px] font-bold px-1 py-0.5 rounded ${METHOD_COLORS[s.method] ?? "bg-muted text-muted-foreground"}`}
              >
                {s.method}
              </span>
            ))}
          </div>
          <span className="text-sm font-semibold text-foreground truncate">{workflow.name}</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />}
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-3 border-t border-border/40">
              <p className="text-xs text-muted-foreground mt-3">{workflow.description}</p>

              {/* Steps */}
              <div className="space-y-1.5">
                {workflow.steps.map((step, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-4 h-4 rounded-full bg-muted flex items-center justify-center text-[9px] font-bold text-muted-foreground shrink-0">
                      {i + 1}
                    </span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ${METHOD_COLORS[step.method] ?? "bg-muted text-muted-foreground"}`}>
                      {step.method}
                    </span>
                    <code className="text-[10px] text-muted-foreground font-mono truncate">{step.path}</code>
                    {step.summary && (
                      <span className="text-[10px] text-muted-foreground truncate hidden sm:block">— {step.summary}</span>
                    )}
                  </div>
                ))}
              </div>

              {/* Claude prompt */}
              <div className="bg-muted/40 rounded-lg px-3 py-2 border border-border/40">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[11px] text-foreground/80 leading-relaxed flex-1 italic">
                    "{workflow.prompt}"
                  </p>
                  <CopyButton text={workflow.prompt} />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

type Props = {
  workflows: Workflow[];
};

const WorkflowSuggestions = ({ workflows }: Props) => {
  if (!workflows || workflows.length === 0) return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mt-6"
    >
      <div className="flex items-center gap-2 mb-3">
        <GitBranch className="w-4 h-4 text-violet-500" />
        <h3 className="text-sm font-semibold text-foreground">Suggested Workflows</h3>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 dark:bg-violet-950/40 text-violet-600 dark:text-violet-400 font-medium">
          {workflows.length} detected
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Common multi-step sequences detected in your spec. Copy the prompt and paste it into Claude or Cursor.
      </p>
      <div className="space-y-2">
        {workflows.map((wf, i) => (
          <WorkflowCard key={wf.name} workflow={wf} index={i} />
        ))}
      </div>
    </motion.section>
  );
};

export default WorkflowSuggestions;
